import React, { useContext, useEffect, useState } from 'react';

import { TaskEntity } from '../../tasks/Task.interface';
import {
  useCreateProductVerification,
  useProductEntityPrefixId,
  useProductViewParams,
  useUpdateProductVerification
} from '../Product.hooks';
import {
  BaseProductBoardColumnProps,
  ProductVerification
} from '../Product.interface';
import { ProductBoardContext } from './ProductBoard.context';
import { ProductBoardColumnEntity } from './ProductBoardColumn';
import ProductBoardItem from './ProductBoardItem';

export type ProductBoardVerificationProps = BaseProductBoardColumnProps<
  ProductVerification,
  'verification'
>;

const ProductBoardVerification: React.FC<ProductBoardVerificationProps> = (
  props: ProductBoardVerificationProps
) => {
  const {
    product,
    collapsed,
    verification: initialVerification,
    onCancel,
    onError,
    onSave
  } = props;
  const entity = ProductBoardColumnEntity.Verification;
  const params: string = useProductViewParams();
  const { toggleSelected, isSelected } = useContext(ProductBoardContext);
  const [verification, setVerification] = useState(initialVerification);
  useEffect(() => {
    if (initialVerification) {
      setVerification(initialVerification);
    }
  }, [initialVerification]);
  const {
    createdProductVerification,
    isCreatingProductVerification,
    createProductVerificationError,
    createProductVerification
  } = useCreateProductVerification(product.id);
  const {
    updatedProductVerification,
    isUpdatingProductVerification,
    updateProductVerification
  } = useUpdateProductVerification(product.id, verification.id || 0);
  const entityId: string = useProductEntityPrefixId(
    verification,
    ProductBoardColumnEntity.Verification
  );

  useEffect(() => {
    if (createdProductVerification) {
      setVerification(createdProductVerification);
      onSave?.(createdProductVerification, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdProductVerification]);

  useEffect(() => {
    if (updatedProductVerification) {
      setVerification(updatedProductVerification);
      onSave?.(updatedProductVerification);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductVerification]);

  useEffect(() => {
    if (createProductVerificationError) {
      onError?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createProductVerificationError]);

  return (
    <ProductBoardItem
      product={product}
      taskEntity={verification as TaskEntity}
      title={verification.title || ''}
      entityId={entityId}
      collapsed={collapsed}
      editable={!verification.id}
      isSelected={isSelected(verification.id, entity)}
      loading={isUpdatingProductVerification || isCreatingProductVerification}
      viewDetailsLink={`/portal/products/${verification.productId}/verifications/${verification.id}${params}`}
      onSelect={() => toggleSelected(verification, entity)}
      onCancel={() => {
        setVerification(initialVerification);
        onCancel?.();
      }}
      onSave={title => {
        verification.title = title;
        if (verification.id) {
          updateProductVerification(verification);
        } else {
          createProductVerification(verification);
        }
      }}
    />
  );
};

export default ProductBoardVerification;
